import React, { useEffect, useState } from "react";
import { Edit3, Loader2, Plus, RefreshCw, Trash2, X } from "lucide-react";
import useBulkOptionGroups from "../../services/Order/useBulkOptionGroups";
import useBulkPackages from "../../services/Order/useBulkPackages";
import useBulkCatalogue from "../../services/Order/useBulkCatalogue";

const EMPTY = {
  option_group_item_id: "",
  option_group_id: "",
  package_id: "",
  item_id: "",
  variant_id: "",
  quantity_per_person: "",
  fixed_quantity: "",
  extra_price: "",
  is_default: "0",
  display_order: "",
  status: "ACTIVE",
};
const field =
  "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-400";

export default function BulkOptionGroupItems() {
  const {
    getOptionGroups,
    getOptionGroupItems,
    insertOptionGroupItem,
    updateOptionGroupItem,
    deleteOptionGroupItem,
    optionGroupItemsLoading,
    optionGroupItemSaving,
    optionGroupItemDeletingId,
  } = useBulkOptionGroups();
  const { getPackages } = useBulkPackages();
  const { getItems, getItemVariants } = useBulkCatalogue();
  const [rows, setRows] = useState([]),
    [groups, setGroups] = useState([]),
    [packages, setPackages] = useState([]),
    [items, setItems] = useState([]),
    [variants, setVariants] = useState([]),
    [form, setForm] = useState(EMPTY),
    [modal, setModal] = useState(false);

  const load = async () => {
    const r = await getOptionGroupItems("");
    setRows(Array.isArray(r) ? r : []);
  };
  const ensureOptions = async () => {
    const [g, p, i, v] = await Promise.all([
      groups.length ? Promise.resolve(groups) : getOptionGroups(""),
      packages.length ? Promise.resolve(packages) : getPackages(""),
      items.length ? Promise.resolve(items) : getItems("", ""),
      variants.length ? Promise.resolve(variants) : getItemVariants(""),
    ]);
    setGroups(Array.isArray(g) ? g : []);
    setPackages(Array.isArray(p) ? p : []);
    setItems(Array.isArray(i) ? i : []);
    setVariants(Array.isArray(v) ? v : []);
  };
  useEffect(() => {
    load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const edit = async (r) => {
    await ensureOptions();
    setForm(
      Object.keys(EMPTY).reduce(
        (a, k) => ({ ...a, [k]: String(r?.[k] ?? EMPTY[k]) }),
        {},
      ),
    );
    setModal(true);
  };
  const submit = async (e) => {
    e.preventDefault();
   const payload = {
  ...form,
  is_default:
    form.is_default === true ||
    form.is_default === 1 ||
    String(form.is_default).toLowerCase() === "true" ||
    String(form.is_default) === "1"
      ? 1
      : 0,
};

const saved = form.option_group_item_id
  ? await updateOptionGroupItem(payload)
  : await insertOptionGroupItem(payload);
    if (saved) {
      setModal(false);
      setForm(EMPTY);
      load();
    }
  };
  const name = (arr, idKey, nameKey, id, label) =>
    arr.find((x) => String(x?.[idKey] ?? x?.id) === String(id))?.[nameKey] ||
    `${label} #${id || "—"}`;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Option Group Items
          </h3>
          <p className="text-[11px] text-slate-500">
            Manage items and variants inside option groups.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            disabled={optionGroupItemsLoading}
            className="inline-flex h-8 items-center gap-1 rounded-lg border px-3 text-xs"
          >
            <RefreshCw
              size={13}
              className={optionGroupItemsLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
          <button
            type="button"
            onClick={async () => {
              await ensureOptions();
              setForm(EMPTY);
              setModal(true);
            }}
            className="inline-flex h-8 items-center gap-1 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white"
          >
            <Plus size={13} />
            Add Item
          </button>
        </div>
      </div>
      {optionGroupItemsLoading ? (
        <div className="h-24 animate-pulse rounded-xl border bg-white" />
      ) : !rows.length ? (
        <div className="rounded-xl border border-dashed bg-white p-12 text-center text-sm text-slate-500">
          No option group items found.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {rows.map((r, i) => {
            const id = String(r?.option_group_item_id ?? "");
            return (
              <div
                key={id || i}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <b className="text-sm">
                    {name(items, "item_id", "item_name", r?.item_id, "Item")}
                  </b>
                  <p className="text-[11px] text-slate-500">
                    ID #{id || "—"} ·{" "}
                    {name(
                      groups,
                      "option_group_id",
                      "group_name",
                      r?.option_group_id,
                      "Group",
                    )}{" "}
                    ·{" "}
                    {name(
                      packages,
                      "package_id",
                      "package_name",
                      r?.package_id,
                      "Package",
                    )}{" "}
                    · Extra ₹{r?.extra_price || "0"} · Default{" "}
                    {r?.is_default || "0"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => edit(r)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  type="button"
                  disabled={optionGroupItemDeletingId === id}
                  onClick={async () => {
                    if (await deleteOptionGroupItem(id)) load();
                  }}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600"
                >
                  {optionGroupItemDeletingId === id ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Trash2 size={13} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
      {modal && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h3 className="font-semibold">
                {form.option_group_item_id
                  ? "Edit Option Group Item"
                  : "Add Option Group Item"}
              </h3>
              <button type="button" onClick={() => setModal(false)}>
                <X size={17} />
              </button>
            </div>
            <form onSubmit={submit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
                <Select
                  label="Option Group"
                  value={form.option_group_id}
                  set={(v) => setForm((p) => ({ ...p, option_group_id: v }))}
                >
                  <option value="">Select Option Group</option>
                  {groups.map((g) => {
                    const id = g?.option_group_id ?? g?.id;
                    return (
                      <option key={id} value={id}>
                        {g?.group_name || `Group #${id}`}
                      </option>
                    );
                  })}
                </Select>
                <Select
                  label="Package"
                  value={form.package_id}
                  set={(v) => setForm((p) => ({ ...p, package_id: v }))}
                >
                  <option value="">Select Package</option>
                  {packages.map((p) => (
                    <option key={p?.package_id} value={p?.package_id}>
                      {p?.package_name || `Package #${p?.package_id}`}
                    </option>
                  ))}
                </Select>
                <Select
                  label="Item"
                  value={form.item_id}
                set={(v) =>
  setForm((p) => ({
    ...p,
    item_id: v,
    variant_id: "",
  }))
}
                >
                  <option value="">Select Item</option>
                  {items.map((i) => (
                    <option key={i?.item_id} value={i?.item_id}>
                      {i?.item_name || `Item #${i?.item_id}`}
                    </option>
                  ))}
                </Select>
                <Select
                  label="Variant"
                  value={form.variant_id}
                  set={(v) => setForm((p) => ({ ...p, variant_id: v }))}
                >
                  <option value="">Select Variant</option>
                {variants
  .filter(
    (v) =>
      String(v?.item_id) === String(form.item_id)
  )
  .map((v) => (
    <option key={v?.variant_id} value={v?.variant_id}>
      {v?.variant_name || `Variant #${v?.variant_id}`}
      {" "}— ID: {v?.variant_id}
    </option>
  ))}
                </Select>
                {[
                  "quantity_per_person",
                  "fixed_quantity",
                  "extra_price",
                  "display_order",
                ].map((k) => (
                  <Input
                    key={k}
                    label={k
                      .split("_")
                      .map((x) => x[0].toUpperCase() + x.slice(1))
                      .join(" ")}
                    name={k}
                    form={form}
                    setForm={setForm}
                  />
                ))}
                <Select
                  label="Is Default"
                  value={form.is_default}
                  set={(v) => setForm((p) => ({ ...p, is_default: v }))}
                >
                  <option value="1">1</option>
                  <option value="0">0</option>
                </Select>
                <Select
                  label="Status"
                  value={form.status}
                  set={(v) => setForm((p) => ({ ...p, status: v }))}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </Select>
              </div>
              <div className="flex justify-end gap-2 border-t bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setModal(false)}
                  className="h-9 rounded-lg border bg-white px-4 text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={optionGroupItemSaving}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white"
                >
                  {optionGroupItemSaving ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : form.option_group_item_id ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}{" "}
                  {optionGroupItemSaving
                    ? "Saving..."
                    : form.option_group_item_id
                      ? "Update"
                      : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
function Select({ label, value, set, children }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => set(e.target.value)}
        className={field}
      >
        {children}
      </select>
    </label>
  );
}
function Input({ label, name, form, setForm }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      <input
        type="number"
        value={form[name]}
        onChange={(e) => setForm((p) => ({ ...p, [name]: e.target.value }))}
        className={field}
      />
    </label>
  );
}
